/* eslint-disable @next/next/no-img-element */
import React from 'react';
import classNames from 'classnames';
import { getFocalPoint } from '../../../utils/images';
import { CarouselItems } from '../HeroCarousel';
import styles from './HeroImage.module.scss';
import NextImage, { getImageProps } from 'next/image';

export type HeroImageProps = {
  alt?: string;
  fetchPriority?: FetchPriorityType;
  loading?: LoadingType;
  className?: string;
} & Pick<CarouselItems, 'croppedLogoUrl480' | 'croppedLogoUrl600' | 'croppedLogoUrl940' | 'croppedLogoUrl1880'>;

export type LoadingType = 'eager' | 'lazy';

export enum FetchPriorityType {
  high = 'high',
  low = 'low',
}

export const HeroImage = ({
  className,
  alt,
  croppedLogoUrl480,
  croppedLogoUrl600,
  croppedLogoUrl940,
  croppedLogoUrl1880,
  fetchPriority,
  loading,
}: HeroImageProps) => {
  const bestAvailableUrl =
    [croppedLogoUrl1880, croppedLogoUrl940, croppedLogoUrl600, croppedLogoUrl480].find(Boolean) ?? '';
  const focalPoint = getFocalPoint(bestAvailableUrl);
  const primaryImageUrl = bestAvailableUrl;
  const fallbackImageUrl = croppedLogoUrl600 || croppedLogoUrl940 || croppedLogoUrl480 || '';
  const imageClasses = classNames(styles.image, className);

  const {
    props: { srcSet, sizes },
  } = getImageProps({
    alt: '',
    sizes: '(max-width:480px) 480px, (max-width:600px) 600px, (max-width:940px) 940px, 1880px',
    width: 1880,
    height: 940,
    src: primaryImageUrl,
  });

  return (
    <picture data-testid="hero-image">
      <source srcSet={srcSet} sizes={sizes} />
      {focalPoint ? (
        <Image
          className={imageClasses}
          alt={alt || ''}
          src={fallbackImageUrl}
          loading={loading}
          fetchPriority={fetchPriority}
          x={focalPoint.x}
          y={focalPoint.y}
          data-testid="hero-img"
        />
      ) : (
        <NextImage
          className={imageClasses}
          width={600}
          height={300}
          alt={alt || 'Hero image'}
          src={fallbackImageUrl}
          loading={loading || (fetchPriority === 'high' ? 'eager' : 'lazy')}
          fetchPriority={fetchPriority}
          data-testid="hero-img"
        />
      )}
    </picture>
  );
};

interface ImageProps {
  x: number;
  y: number;
  className?: string;
  alt: string;
  src?: string;
  loading?: LoadingType;
  fetchPriority?: FetchPriorityType;
  'data-testid'?: string;
}

const Image: React.FC<ImageProps> = ({ x, y, className, src, fetchPriority, loading, alt, ...props }) => {
  const imageStyle = {
    objectPosition: `${x * 100}% ${y * 100}%`,
  };

  if (!src) {
    return null;
  }

  return (
    <NextImage
      className={className}
      style={imageStyle}
      src={src}
      width={600}
      height={300}
      loading={loading || (fetchPriority === 'high' ? 'eager' : 'lazy')}
      fetchPriority={fetchPriority}
      {...props}
      alt={alt || ''}
    />
  );
};
